import { useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { supabase, initialAuthHash, initialAuthSearch } from "../lib/supabase";
import { withTimeout } from "../lib/api";
import { Button } from "../components/ui/Button";
import { Field, Input } from "../components/ui/Input";

// Landing page for invite / recovery links.
//
// Two coexisting flows:
//
//   1. Custom scanner-resistant token flow (current default).
//      send-invite mints a UUID, stores it in pending_invites, and
//      embeds a URL like `/#/accept-invite?invite=<UUID>` in the
//      email body via the customised Supabase template. The URL
//      carries NO Supabase auth credential — corporate email
//      scanners can GET it as many times as they want and nothing
//      is consumed. We call lookup-invite (read-only) to display
//      the officer's name; the token is only burned when the
//      officer SUBMITS the password form, which fires redeem-invite.
//
//   2. Legacy Supabase token flows (still supported for any emails
//      sent before the template was updated, and for recovery /
//      magic-link links which we haven't migrated). These come in
//      three URL shapes — #access_token=…, ?code=…, ?token_hash=… —
//      all of which detectSessionInUrl-in-the-SDK is disabled for so
//      we own the redeem here.
export function AcceptInvite() {
  const inviteToken = parseInviteTokenFromUrl();
  return inviteToken
    ? <CustomInviteFlow token={inviteToken} />
    : <LegacySupabaseFlow />;
}

// HashRouter URL: window.location.hash holds something like
// "#/accept-invite?invite=<UUID>" — the "search" portion lives
// inside the hash, not in window.location.search.
function parseInviteTokenFromUrl(): string | null {
  const hash = initialAuthHash;
  const idx = hash.indexOf("?");
  if (idx === -1) return null;
  const params = new URLSearchParams(hash.slice(idx + 1));
  const t = params.get("invite");
  if (!t) return null;
  if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(t)) {
    return null;
  }
  return t;
}

// Supabase edge functions live under /functions/v1/<name>. We hit
// lookup-invite / redeem-invite directly via fetch (instead of the
// supabase-js client) because neither requires a JWT and the simpler
// fetch path avoids the SDK's auto-injected auth header that some
// edge-function configurations reject when verify_jwt=false.
async function callEdge(name: string, payload: unknown): Promise<{ ok: true; data: Record<string, unknown> } | { ok: false; error: string }> {
  const baseUrl = import.meta.env.VITE_SUPABASE_URL as string | undefined;
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined;
  if (!baseUrl || !anonKey) {
    return { ok: false, error: "Cloud features not configured." };
  }
  try {
    const res = await fetch(`${baseUrl}/functions/v1/${name}`, {
      method: "POST",
      headers: {
        "content-type": "application/json",
        apikey: anonKey,
      },
      body: JSON.stringify(payload),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) {
      return { ok: false, error: (body as { error?: string }).error ?? `HTTP ${res.status}` };
    }
    return { ok: true, data: body as Record<string, unknown> };
  } catch (e) {
    return { ok: false, error: (e as Error).message ?? "Network error" };
  }
}

function CustomInviteFlow({ token }: { token: string }) {
  const [loading, setLoading] = useState(true);
  const [invitedName, setInvitedName] = useState<string | null>(null);
  const [emailMasked, setEmailMasked] = useState<string | null>(null);
  const [password, setPassword] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const res = await callEdge("lookup-invite", { token });
      if (cancelled) return;
      if (!res.ok) {
        setError(res.error);
      } else {
        setInvitedName((res.data.invitedName as string | null) ?? null);
        setEmailMasked((res.data.emailMasked as string | null) ?? null);
      }
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [token]);

  const redeemMut = useMutation({
    mutationFn: async () => {
      if (password.length < 8) throw new Error("Password must be at least 8 characters.");
      if (password !== confirmPw) throw new Error("Passwords don't match.");
      const res = await callEdge("redeem-invite", { token, password });
      if (!res.ok) throw new Error(res.error);
      const email = res.data.email as string;
      // Sign in with the just-set password so the rest of the app
      // (api.currentUser, the auth state listener in App.tsx) picks
      // up the new session cleanly.
      const { error: signInErr } = await withTimeout(
        supabase.auth.signInWithPassword({ email, password }),
        10_000,
        "sign in",
      );
      if (signInErr) throw new Error(signInErr.message);
    },
    onSuccess: () => {
      // Reload at a clean hash route. AcceptInvite's gate
      // (isInviteFlow) snapshots the URL at module load — a real
      // reload is the only way to recompute it without the invite
      // token in the URL.
      const base = import.meta.env.BASE_URL || "/";
      history.replaceState(null, "", `${base}#/`);
      window.location.reload();
    },
    onError: (e: { message?: string }) => setError(e?.message ?? "Failed."),
  });

  return (
    <div className="h-full w-full flex items-center justify-center bg-slate-100">
      <form
        onSubmit={(e) => { e.preventDefault(); setError(null); redeemMut.mutate(); }}
        className="w-full max-w-sm bg-white border border-slate-200 rounded-lg shadow-sm p-6 space-y-4"
      >
        <div>
          <h1 className="text-lg font-semibold text-slate-900">Accept invite</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            {loading
              ? "Loading invite…"
              : error
                ? "Invite could not be loaded."
                : invitedName
                  ? `Welcome, ${invitedName}. Set a password for ${emailMasked}.`
                  : `Set a password for ${emailMasked}.`}
          </p>
        </div>
        {!loading && !error && (
          <>
            <Field label="New password (min 8 characters)">
              <Input
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </Field>
            <Field label="Confirm password">
              <Input
                type="password"
                autoComplete="new-password"
                value={confirmPw}
                onChange={(e) => setConfirmPw(e.target.value)}
              />
            </Field>
          </>
        )}
        {error && <div className="text-sm text-red-600">{error}</div>}
        {!loading && !error && (
          <div className="pt-1 flex">
            <Button type="submit" disabled={redeemMut.isPending} className="w-full">
              {redeemMut.isPending ? "Saving…" : "Set password & continue"}
            </Button>
          </div>
        )}
      </form>
    </div>
  );
}

// Legacy Supabase-token flow — kept for in-flight emails sent before
// the template change, and for recovery / magic-link URLs that still
// use the stock Supabase verify path. Same behaviour as before the
// scanner-resistant refactor.
function LegacySupabaseFlow() {
  const [sessionReady, setSessionReady] = useState(false);
  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let stopPolling: number | null = null;

    function markReady(userEmail: string | null | undefined) {
      if (cancelled) return;
      setEmail(userEmail ?? "");
      setSessionReady(true);
      if (stopPolling) clearInterval(stopPolling);
    }

    async function consumeAuthParams() {
      const hashParams = new URLSearchParams(
        initialAuthHash.replace(/^#/, ""),
      );
      const queryParams = new URLSearchParams(initialAuthSearch);

      const errDesc =
        queryParams.get("error_description") ??
        hashParams.get("error_description") ??
        queryParams.get("error") ??
        hashParams.get("error");
      if (errDesc && !cancelled) {
        const pretty = decodeURIComponent(errDesc.replace(/\+/g, " "));
        setError(
          `${pretty}. Common cause: a corporate email scanner clicked the `
          + `link before you could — ask your supervisor to resend (the new `
          + `invite link is scanner-safe). If the resend still fails your `
          + `IT may need to allow ${new URL(window.location.href).hostname} `
          + `/ supabase.co.`,
        );
        if (stopPolling) clearInterval(stopPolling);
        return;
      }

      const accessToken = hashParams.get("access_token");
      const refreshToken = hashParams.get("refresh_token");
      if (accessToken && refreshToken) {
        const { error } = await supabase.auth.setSession({
          access_token: accessToken,
          refresh_token: refreshToken,
        });
        if (error && !cancelled) setError(error.message);
        return;
      }

      const code = queryParams.get("code");
      if (code) {
        const { error: exErr } = await supabase.auth.exchangeCodeForSession(code);
        if (exErr && !cancelled) setError(exErr.message);
        return;
      }

      const tokenHash = queryParams.get("token_hash") ?? hashParams.get("token_hash");
      const type = queryParams.get("type") ?? hashParams.get("type") ?? "invite";
      if (tokenHash) {
        const { error: vErr } = await supabase.auth.verifyOtp({
          token_hash: tokenHash,
          type: type as "invite" | "recovery" | "magiclink" | "signup" | "email",
        });
        if (vErr && !cancelled) setError(vErr.message);
      }
    }

    const { data: sub } = supabase.auth.onAuthStateChange((_evt, session) => {
      if (session?.user) markReady(session.user.email);
    });

    void consumeAuthParams();
    supabase.auth.getSession().then(({ data }) => {
      if (data.session?.user) markReady(data.session.user.email);
    });

    const deadline = Date.now() + 10_000;
    stopPolling = window.setInterval(async () => {
      if (Date.now() > deadline) {
        if (!sessionReady && !cancelled) {
          setError("Invite link is invalid or expired. Ask your supervisor to resend.");
        }
        if (stopPolling) clearInterval(stopPolling);
        return;
      }
      const { data } = await supabase.auth.getSession();
      if (data.session?.user) markReady(data.session.user.email);
    }, 500);

    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
      if (stopPolling) clearInterval(stopPolling);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setPasswordMut = useMutation({
    mutationFn: async () => {
      if (password.length < 8) throw new Error("Password must be at least 8 characters.");
      if (password !== confirmPw) throw new Error("Passwords don't match.");
      const { error: updErr } = await withTimeout(
        supabase.auth.updateUser({ password }),
        10_000,
        "set password",
      );
      if (updErr) throw new Error(updErr.message);
    },
    onSuccess: () => {
      const base = import.meta.env.BASE_URL || "/";
      history.replaceState(null, "", `${base}#/`);
      window.location.reload();
    },
    onError: (e: { message?: string }) => setError(e?.message ?? "Failed."),
  });

  return (
    <div className="h-full w-full flex items-center justify-center bg-slate-100">
      <form
        onSubmit={(e) => { e.preventDefault(); setError(null); setPasswordMut.mutate(); }}
        className="w-full max-w-sm bg-white border border-slate-200 rounded-lg shadow-sm p-6 space-y-4"
      >
        <div>
          <h1 className="text-lg font-semibold text-slate-900">Accept invite</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            {sessionReady
              ? `Set a password for ${email}.`
              : error
                ? "Invite could not be loaded."
                : "Loading invite…"}
          </p>
        </div>
        {sessionReady && (
          <>
            <Field label="New password (min 8 characters)">
              <Input
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </Field>
            <Field label="Confirm password">
              <Input
                type="password"
                autoComplete="new-password"
                value={confirmPw}
                onChange={(e) => setConfirmPw(e.target.value)}
              />
            </Field>
          </>
        )}
        {error && <div className="text-sm text-red-600">{error}</div>}
        {sessionReady && (
          <div className="pt-1 flex">
            <Button type="submit" disabled={setPasswordMut.isPending} className="w-full">
              {setPasswordMut.isPending ? "Saving…" : "Set password & continue"}
            </Button>
          </div>
        )}
      </form>
    </div>
  );
}
